import { applicationWWWXRLEncodedBodyBuilder } from '../utils/body-builder';
import { asyncHandler } from './async-handler';
import { RequestInfo } from '../../interfaces/RequestInfo';
import store from '../../store/store';
import {
  setAccessToken,
  setAgencyAccessToken,
} from '../../store/reducers/authSlice';
import moment from 'moment';
import {
  setAiOnboardingInprogress,
  updateOnboardingStage,
  updateProfile,
} from '../../store/reducers/userSlice';
import { REGISTRATION_API } from '../constants/central-api';
import { submitLogToBugsnag } from './log';
import { stepsAndStagesMap } from '../constants/steps_stages_map';
import { t } from 'i18next';
import { setMessage } from '../../store/reducers/appSlice';
import { loadSignUpMerchant } from './signup-merchant';
import { fetchAppInit } from './app-init';

type RegistrationData = {
  email: string;
  password: string;
};

function showSignUpFailed() {
  store.dispatch(
    setMessage({ message: t('SignUpFailed'), messageType: 'error' })
  );
}

export async function requestUserSignUp(registrationData: RegistrationData) {
  const merchantSlug = store.getState().user.merchant.merchant_slug;
  const appLanguage = store.getState().app.language;

  // Without a merchant the URL would be `merchants//sellers`, which can only
  // answer 404. Say so, and load the merchant so the next attempt can work.
  if (!merchantSlug) {
    store.dispatch(
      setMessage({ message: t('SignUpNotReady'), messageType: 'warning' })
    );
    void loadSignUpMerchant();
    return false;
  }

  try {
    const requestInfo: RequestInfo = {
      url: REGISTRATION_API(merchantSlug),
      method: 'POST',
      body: applicationWWWXRLEncodedBodyBuilder(registrationData),
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/x-www-form-urlencoded',
      },
      // Phase B — registration mints an agency key + sets the session cookie;
      // include credentials so the browser stores it (parity with login).
      credentials: 'include',
    };

    const actionType = 'REGISTRATION_VERIFICATION';
    const customMsg = 'Registration Successful';

    // No success toast, but every error answer is shown: asyncHandler shows
    // the API's message for a `success: false` body of any status. A visitor
    // has no session, so a 401 here must not be read as an expired one.
    const res: any = await asyncHandler(
      requestInfo,
      actionType,
      customMsg,
      false,
      true,
      true
    );

    // No usable answer (network failure, or a body without a `success` flag):
    // asyncHandler showed nothing, so say it here.
    if (!res?.data) {
      showSignUpFailed();
      return false;
    }

    const response = res.data;

    if (res.actionType === actionType && response.success) {
      const token_type = 'Bearer';
      const current_step = 'step_1';
      const { access_token, user, agency_api_key } = response?.data;
      if (access_token && access_token !== '') {
        // Set the access token
        store.dispatch(
          setAccessToken({
            sellerToken: access_token,
            type: token_type,
            expiresOn: moment().add(21, 'days'),
          })
        );

        // Phase 11-Bridge: store the agency-portal employee key minted alongside
        // the JWT so newly-registered sellers land directly on the new portal.
        // The redirect to /agency happens in the calling SignUp component,
        // gated on the agency_api_key being present in the response.
        if (agency_api_key && agency_api_key !== '') {
          store.dispatch(setAgencyAccessToken(agency_api_key));
        }

        if (user?.seller_id) {
          // set user profile data
          store.dispatch(
            updateProfile({
              seller_id: user.seller_id,
              user_id: user.id ? user.id?.toString() : '',
              email: registrationData.email,
              language: appLanguage,
            })
          );

          store.dispatch(
            updateOnboardingStage(stepsAndStagesMap[current_step].name)
          );

          //* Added below for the MCP Launch
          store.dispatch(setAiOnboardingInprogress(true));
        }

        // The new seller token puts the app's boot splash up until seller-init
        // has succeeded (`!isInitSuccess && hasSellerSession` in App.tsx), and
        // nothing else loads it for a fresh account: the boot-time init is
        // skipped while there is no seller token. Without this call the splash
        // never cleared and /register sat on it until the page was reloaded.
        // Same as the Google sign-up and the login (`normalLoginProcess`).
        void fetchAppInit();
        return true;
      }
      return false;
    } else {
      console.log(response.message);
      return false;
    }
  } catch (exception) {
    console.log('Error during authentication request');
    showSignUpFailed();
    submitLogToBugsnag('error', `${exception}`);
    return false;
  }
}
